import React from 'react';
import { useRef, useEffect } from 'react';
import { Box, Typography, Divider, useMediaQuery } from '@mui/material';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useTheme } from '@mui/material/styles';

import THEME from '../../../constants/theme';

/**
 * StyledList component for displaying lists with customizable styling
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of list items with title and text
 * @param {string} props.variant - List style variant (default, blue, numbered)
 * @param {string} props.margin - Custom margin for the list container
 * @param {Object} props.additionalContent - Map of additional content indexed by item position
 * @returns {React.Component} StyledList component
 */
const StyledList = ({
  items = [],
  variant = 'default',
  margin = '2rem 0 0 0',
  additionalContent = {},
  titleTag = 'h3',
  titleVariant = 'h3',
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const listRef = useRef(null);
  const controls = useAnimation();
  const isInView = useInView(listRef, { threshold: 0.5 });
  const isBlueVariant = variant === 'blue';
  const isNumbered = variant === 'numbered';

  useEffect(() => {
    controls.start(isInView ? 'visible' : 'hidden');
  }, [isInView, controls]);

  // Helper to parse **bold** and ##Program Name## and return an array of React elements
  function renderWithBoldAndPrograms(text) {
    if (!text) return null;
    const parts = [];
    let lastIdx = 0;
    // Corrected regex for **bold** or ##Program Name##
    const combinedRegex = /\*\*([^*]+)\*\*|##([^#]+)##/g;
    let key = 0;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        parts.push(text.slice(lastIdx, match.index));
      }

      if (match[1]) {
        // Matched **bold**
        parts.push(<strong key={`bold-${key++}`}>{match[1]}</strong>);
      } else if (match[2]) {
        // Matched ##Program Name##
        parts.push(
          <Typography
            component='span'
            key={`program-${key++}`}
            sx={{
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: '1.1em' // Slightly larger font size
            }}
          >
            {match[2]}
          </Typography>
        );
      }
      lastIdx = match.index + match[0].length;
    }

    if (lastIdx < text.length) {
      parts.push(text.slice(lastIdx));
    }
    // Wrap parts in React.Fragment with unique keys if parts array is not empty
    return parts.length > 0
      ? parts.map((part, index) => (
          <React.Fragment key={`part-${index}`}>{part}</React.Fragment>
        ))
      : text; // Return original text if no matches found
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } }
  };

  return (
    <Box
      ref={listRef}
      component={motion.ul}
      initial='hidden'
      animate={controls}
      variants={containerVariants}
      sx={{
        listStyle: 'none',
        padding: 0,
        margin,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        alignItems: isMobile ? 'center' : 'stretch', // Center items on mobile
        // Remove left padding/margin on mobile, allow normal on desktop
        pl: isMobile ? 0 : undefined,
        ...props.sx
      }}
    >
      {items.map((item, index) => {
        const itemTitle = item.title || (item.TITLE ? item.TITLE : null);
        const itemText = item.text || item.TEXT;
        const hasAdditionalContent =
          additionalContent && additionalContent[index];

        return (
          <Box
            component={motion.li}
            key={index}
            variants={itemVariants}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '0',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '500px' : 'none',
              '&:hover': {
                '& .list-item-dot': {
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE
                },
                '& .list-item-title': {
                  color: THEME.COLORS.PRIMARY_BLUE
                },
                '& .list-item-title-text': {
                  color: THEME.COLORS.PRIMARY_BLUE
                },
                '& .list-item-text': {
                  color: isBlueVariant
                    ? THEME.COLORS.PRIMARY_BLUE
                    : variant === 'default'
                      ? THEME.COLORS.PRIMARY_BLUE
                      : THEME.COLORS.NEUTRAL_BLACK
                }
              }
            }}
          >
            {/* With Title and Text */}
            {itemTitle ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    paddingTop: '0'
                  }}
                >
                  <Box
                    component='span'
                    className='list-item-dot'
                    sx={{
                      width: '7px',
                      height: '7px',
                      backgroundColor: isBlueVariant
                        ? THEME.COLORS.PRIMARY_BLUE
                        : THEME.COLORS.NEUTRAL_GRAY,
                      borderRadius: '50%',
                      flexShrink: 0,
                      margin: isMobile ? '0.5rem 0 0 0' : '0.5rem 0 0 1rem',
                      transition: 'background-color 0.3s ease'
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%'
                    }}
                  >
                    <Typography
                      variant={item.titleVariant || titleVariant}
                      component={item.titleTag || titleTag}
                      className='list-item-title'
                      sx={{
                        fontSize: '1.2rem',
                        color: isBlueVariant
                          ? THEME.COLORS.PRIMARY_BLUE
                          : THEME.COLORS.NEUTRAL_GRAY_DARK,
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        marginBottom: '0.5rem',
                        marginTop: '0',
                        transition: 'color 0.3s ease',
                        lineHeight: 1.2
                      }}
                    >
                      {itemTitle}
                    </Typography>

                    <Typography
                      variant='body1'
                      className='list-item-text'
                      sx={{
                        fontSize: '1.3rem',
                        color: isBlueVariant
                          ? THEME.COLORS.PRIMARY_BLUE
                          : THEME.COLORS.NEUTRAL_BLACK,
                        lineHeight: 1.4,
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                        transition: 'color 0.3s ease',
                        mt: 0
                      }}
                    >
                      {itemText &&
                        itemText.split('\n').map((textPart, textIndex) => {
                          if (textIndex === 0) {
                            return (
                              <React.Fragment key={textIndex}>
                                {renderWithBoldAndPrograms(textPart)}
                              </React.Fragment>
                            );
                          }
                          return (
                            <Box
                              key={textIndex}
                              component='span'
                              sx={{
                                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                                fontFamily:
                                  THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                                color: THEME.COLORS.PRIMARY_BLUE,
                                display: 'block',
                                fontSize: '1.3rem',
                                paddingLeft: '2rem',
                                position: 'relative',
                                lineHeight: 1.4,
                                '&::before': {
                                  content: '"-"',
                                  position: 'absolute',
                                  left: 0
                                }
                              }}
                            >
                              {renderWithBoldAndPrograms(textPart)}
                            </Box>
                          );
                        })}
                    </Typography>

                    {/* Additional content slot */}
                    {hasAdditionalContent && (
                      <Box sx={{ mt: 3, width: '100%' }}>
                        {additionalContent[index]}
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              /* Text Only */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  paddingTop: '0',
                  width: '100%'
                }}
              >
                {!isNumbered ? (
                  <Box
                    component='span'
                    className='list-item-dot'
                    sx={{
                      width: '7px',
                      height: '7px',
                      backgroundColor: isBlueVariant
                        ? THEME.COLORS.PRIMARY_BLUE
                        : THEME.COLORS.NEUTRAL_GRAY,
                      borderRadius: '50%',
                      flexShrink: 0,
                      margin: isMobile ? '0.5rem 0 0 0' : '0.5rem 0 0 1rem',
                      transition: 'background-color 0.3s ease'
                    }}
                  />
                ) : (
                  <Typography
                    variant='h6'
                    className='list-item-title'
                    sx={{
                      color: isBlueVariant
                        ? THEME.COLORS.PRIMARY_BLUE
                        : THEME.COLORS.NEUTRAL_BLACK,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      marginRight: '0.5rem',
                      minWidth: '1.5rem',
                      textAlign: 'center',
                      transition: 'color 0.3s ease',
                      lineHeight: 1.2,
                      marginTop: '0'
                    }}
                  >
                    {index + 1}.
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                  }}
                >
                  <Typography
                    variant='body1'
                    className='list-item-text'
                    sx={{
                      fontSize: '1.6rem', // Base font size for text-only items
                      color: isBlueVariant
                        ? THEME.COLORS.PRIMARY_BLUE
                        : THEME.COLORS.NEUTRAL_BLACK, // Base color
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.REGULAR, // Base weight
                      lineHeight: 1.4,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY, // Base family                                            paddingRight: "1.5rem",
                      transition: 'color 0.3s ease',
                      mt: 0
                    }}
                  >
                    {itemText &&
                      itemText.split('\n').map((textPart, textIndex) => {
                        if (textIndex === 0) {
                          // Check if this is a numbered variant and if the text contains a colon
                          if (isNumbered && textPart.includes(':')) {
                            const colonIndex = textPart.indexOf(':');
                            const beforeColon = textPart.substring(
                              0,
                              colonIndex
                            );
                            const afterColon = textPart.substring(colonIndex);

                            return (
                              <React.Fragment key={textIndex}>
                                {' '}
                                <Typography
                                  component='span'
                                  className='list-item-title-text'
                                  sx={{
                                    fontWeight:
                                      THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                                    fontSize: 'inherit', // Inherit the same font size as parent
                                    transition: 'color 0.3s ease'
                                  }}
                                >
                                  {renderWithBoldAndPrograms(beforeColon)}
                                </Typography>
                                {renderWithBoldAndPrograms(afterColon)}
                              </React.Fragment>
                            );
                          }
                          return (
                            <React.Fragment key={textIndex}>
                              {renderWithBoldAndPrograms(textPart)}
                            </React.Fragment>
                          );
                        }
                        return (
                          <Box
                            key={textIndex}
                            component='span'
                            sx={{
                              // Ensure styles match the parent Typography for consistency
                              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.REGULAR, // Match parent Typography
                              fontFamily:
                                THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY, // Match parent Typography
                              color: isBlueVariant // Match parent Typography color logic
                                ? THEME.COLORS.PRIMARY_BLUE
                                : THEME.COLORS.NEUTRAL_BLACK,
                              fontSize: '1.6rem', // Explicitly same as parent Typography
                              lineHeight: 1.4, // Match parent Typography

                              // Existing layout styles for subsequent lines
                              display: 'block',
                              paddingLeft: '2rem', // Indentation for sub-lines
                              position: 'relative',
                              mt: '0.5rem', // Margin top for sub-lines
                              '&::before': {
                                content: '"-"',
                                position: 'absolute',
                                left: 0
                              }
                            }}
                          >
                            {renderWithBoldAndPrograms(textPart)}
                          </Box>
                        );
                      })}
                  </Typography>

                  {/* Additional content slot */}
                  {hasAdditionalContent && (
                    <Box sx={{ mt: 3, width: '100%' }}>
                      {additionalContent[index]}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Divider for all variants */}
            {index < items.length - 1 && (
              <Divider
                sx={{
                  width: '100%',
                  marginTop: hasAdditionalContent ? '2rem' : '1rem',
                  marginBottom: '1rem',
                  borderBottomWidth: isBlueVariant ? '1px' : '2px',
                  borderColor: isBlueVariant
                    ? THEME.COLORS.PRIMARY_BLUE
                    : THEME.COLORS.NEUTRAL_GRAY_LIGHT,
                  opacity: isBlueVariant ? 0.5 : 0.3
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default StyledList;
